import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';

import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $isHeadingNode, type HeadingTagType } from '@lexical/rich-text';
import { Bold, Italic, Link, Loader2, Paperclip, Strikethrough } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { cn } from '@/lib/utils';

type ToolbarState = Readonly<{
  blockType: 'paragraph' | HeadingTagType;
  bold: boolean;
  italic: boolean;
  strikethrough: boolean;
  link: boolean;
}>;

function getSelectedNode(selection: ReturnType<typeof $getSelection>) {
  if (!$isRangeSelection(selection)) {
    return null;
  }
  const anchor = selection.anchor.getNode();
  const focus = selection.focus.getNode();
  if (anchor === focus) {
    return anchor;
  }
  return selection.isBackward() ? focus : anchor;
}

function readToolbarState(): ToolbarState {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return {
      blockType: 'paragraph',
      bold: false,
      italic: false,
      strikethrough: false,
      link: false,
    };
  }

  const selectedNode = getSelectedNode(selection);
  const parent = selectedNode?.getParent() ?? null;
  const blockNode = $isHeadingNode(selectedNode)
    ? selectedNode
    : $isHeadingNode(parent)
      ? parent
      : null;
  const isLink =
    (selectedNode !== null && $isLinkNode(selectedNode)) ||
    (parent !== null && $isLinkNode(parent));
  return {
    blockType: blockNode?.getTag() ?? 'paragraph',
    bold: selection.hasFormat('bold'),
    italic: selection.hasFormat('italic'),
    strikethrough: selection.hasFormat('strikethrough'),
    link: isLink,
  };
}

function shallowEqualToolbar(a: ToolbarState, b: ToolbarState): boolean {
  return (
    a.blockType === b.blockType &&
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.strikethrough === b.strikethrough &&
    a.link === b.link
  );
}

export type ToolbarPluginProps = {
  disabled?: boolean;
  className?: string;
  mode?: 'basic' | 'emphasis';
  children?: ReactNode;
};

export function ToolbarPlugin({
  disabled = false,
  className,
  mode = 'basic',
  children,
}: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();

  const [state, setState] = useState<ToolbarState>(() => ({
    blockType: 'paragraph',
    bold: false,
    italic: false,
    strikethrough: false,
    link: false,
  }));

  const updateFromEditor = useCallback(() => {
    editor.getEditorState().read(() => {
      const next = readToolbarState();
      setState((prev) => (shallowEqualToolbar(prev, next) ? prev : next));
    });
  }, [editor]);

  useEffect(() => {
    // Initial read.
    updateFromEditor();

    const unregisterUpdate = editor.registerUpdateListener(() => {
      updateFromEditor();
    });

    const unregisterSelectionChange = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateFromEditor();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      unregisterUpdate();
      unregisterSelectionChange();
    };
  }, [editor, updateFromEditor]);

  const toggleBold = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  }, [editor]);

  const setBlockType = useCallback(
    (blockType: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }

        $setBlocksType(selection, () =>
          blockType === 'paragraph'
            ? $createParagraphNode()
            : $createHeadingNode(blockType as HeadingTagType)
        );
      });
    },
    [editor]
  );

  const toggleItalic = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  }, [editor]);

  const toggleStrikethrough = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  }, [editor]);

  const toggleLink = useCallback(() => {
    if (state.link) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return;
    }
    const url = window.prompt('Link URL');
    if (url === null) {
      return;
    }
    const trimmed = url.trim();
    if (trimmed === '') {
      return;
    }
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, trimmed);
  }, [editor, state.link]);

  return (
    <div
      className={cn(
        'mb-2 flex min-h-8 w-full flex-wrap items-center gap-1 text-muted-foreground',
        className
      )}
      aria-disabled={disabled}
    >
      <Button
        type="button"
        size="sm"
        variant={state.bold ? 'secondary' : 'ghost'}
        className={toolbarButtonClass}
        onClick={toggleBold}
        disabled={disabled}
        aria-label="Bold"
      >
        <Bold className="size-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant={state.italic ? 'secondary' : 'ghost'}
        className={toolbarButtonClass}
        onClick={toggleItalic}
        disabled={disabled}
        aria-label="Italic"
      >
        <Italic className="size-4" />
      </Button>
      {mode === 'basic' ? (
        <>
          <Button
            type="button"
            size="sm"
            variant={state.strikethrough ? 'secondary' : 'ghost'}
            className={toolbarButtonClass}
            onClick={toggleStrikethrough}
            disabled={disabled}
            aria-label="Strikethrough"
          >
            <Strikethrough className="size-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={state.link ? 'secondary' : 'ghost'}
            className={toolbarButtonClass}
            onClick={toggleLink}
            disabled={disabled}
            aria-label="Link"
          >
            <Link className="size-4" />
          </Button>
        </>
      ) : null}
      {mode === 'basic' ? (
        <>
          <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
          <Select value={state.blockType} onValueChange={setBlockType} disabled={disabled}>
            <SelectTrigger
              size="sm"
              className="h-8 w-32 rounded-md px-2 text-xs"
              aria-label="Text style"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paragraph">Normal text</SelectItem>
              <SelectItem value="h1">Heading 1</SelectItem>
              <SelectItem value="h2">Heading 2</SelectItem>
              <SelectItem value="h3">Heading 3</SelectItem>
            </SelectContent>
          </Select>
        </>
      ) : null}
      {children}
    </div>
  );
}

const toolbarButtonClass = 'h-7 w-7 px-0';

export type AttachmentUploadState = 'idle' | 'uploading';

export type AttachmentToolbarButtonProps = {
  disabled?: boolean;
  onUpload(file: File): void | Promise<void>;
  uploadState?: AttachmentUploadState;
};

export function AttachmentToolbarButton({
  disabled = false,
  onUpload,
  uploadState = 'idle',
}: AttachmentToolbarButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file === undefined) {
        return;
      }
      void onUpload(file);
    },
    [onUpload]
  );

  const isUploading = uploadState === 'uploading';

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className={toolbarButtonClass}
        onClick={openFilePicker}
        disabled={disabled || isUploading}
        aria-label="Upload attachment"
      >
        {isUploading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Paperclip className="size-4" />
        )}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*"
        className="sr-only"
        onChange={handleFileChange}
        aria-label="Upload attachment"
      />
    </>
  );
}
